'use client';

import { createStore, clearPersistedStores } from './utils';
import { api } from '@/lib/api';
import { setTokens } from '@/lib/auth';

/**
 * Tenant roles for role-based access control
 */
export type TenantRole =
  | 'SUPER_ADMIN'
  | 'HR_ADMIN'
  | 'HR_MANAGER'
  | 'PAYROLL_ADMIN'
  | 'BENEFITS_ADMIN'
  | 'RECRUITER'
  | 'MANAGER'
  | 'EMPLOYEE';

/**
 * User profile data from the current authenticated session
 */
export interface UserProfile {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  mfaEnabled: boolean;
  emailVerified: boolean;
  isPlatformAdmin: boolean;
}

/**
 * Tenant context for the current session
 */
export interface TenantContext {
  id: string;
  slug: string;
  name: string;
  role: TenantRole;
  employeeId: string | null;
  onboardingCompleted?: boolean;
  plan?: 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  logoUrl?: string;
}

/**
 * Permission check types
 */
export type PermissionAction = 'read' | 'create' | 'update' | 'delete' | 'manage';
export type PermissionResource =
  | 'employees'
  | 'departments'
  | 'locations'
  | 'jobs'
  | 'recruiting'
  | 'payroll'
  | 'benefits'
  | 'performance'
  | 'learning'
  | 'reports'
  | 'settings'
  | 'audit';

/**
 * Role-based permission matrix
 */
const rolePermissions: Record<TenantRole, Record<PermissionResource, PermissionAction[]>> = {
  SUPER_ADMIN: {
    employees: ['read', 'create', 'update', 'delete', 'manage'],
    departments: ['read', 'create', 'update', 'delete', 'manage'],
    locations: ['read', 'create', 'update', 'delete', 'manage'],
    jobs: ['read', 'create', 'update', 'delete', 'manage'],
    recruiting: ['read', 'create', 'update', 'delete', 'manage'],
    payroll: ['read', 'create', 'update', 'delete', 'manage'],
    benefits: ['read', 'create', 'update', 'delete', 'manage'],
    performance: ['read', 'create', 'update', 'delete', 'manage'],
    learning: ['read', 'create', 'update', 'delete', 'manage'],
    reports: ['read', 'create', 'update', 'delete', 'manage'],
    settings: ['read', 'create', 'update', 'delete', 'manage'],
    audit: ['read', 'manage'],
  },
  HR_ADMIN: {
    employees: ['read', 'create', 'update', 'delete', 'manage'],
    departments: ['read', 'create', 'update', 'delete'],
    locations: ['read', 'create', 'update', 'delete'],
    jobs: ['read', 'create', 'update', 'delete'],
    recruiting: ['read', 'create', 'update', 'delete', 'manage'],
    payroll: ['read', 'create', 'update'],
    benefits: ['read', 'create', 'update', 'delete', 'manage'],
    performance: ['read', 'create', 'update', 'delete', 'manage'],
    learning: ['read', 'create', 'update', 'delete', 'manage'],
    reports: ['read', 'create'],
    settings: ['read', 'update'],
    audit: ['read'],
  },
  HR_MANAGER: {
    employees: ['read', 'create', 'update'],
    departments: ['read', 'update'],
    locations: ['read'],
    jobs: ['read', 'create', 'update'],
    recruiting: ['read', 'create', 'update'],
    payroll: ['read'],
    benefits: ['read', 'update'],
    performance: ['read', 'create', 'update'],
    learning: ['read', 'create', 'update'],
    reports: ['read'],
    settings: ['read'],
    audit: ['read'],
  },
  PAYROLL_ADMIN: {
    employees: ['read'],
    departments: ['read'],
    locations: ['read'],
    jobs: ['read'],
    recruiting: [],
    payroll: ['read', 'create', 'update', 'delete', 'manage'],
    benefits: ['read', 'update'],
    performance: [],
    learning: [],
    reports: ['read', 'create'],
    settings: ['read'],
    audit: ['read'],
  },
  BENEFITS_ADMIN: {
    employees: ['read'],
    departments: ['read'],
    locations: ['read'],
    jobs: ['read'],
    recruiting: [],
    payroll: ['read'],
    benefits: ['read', 'create', 'update', 'delete', 'manage'],
    performance: [],
    learning: [],
    reports: ['read', 'create'],
    settings: ['read'],
    audit: ['read'],
  },
  RECRUITER: {
    employees: ['read'],
    departments: ['read'],
    locations: ['read'],
    jobs: ['read', 'create', 'update'],
    recruiting: ['read', 'create', 'update', 'delete', 'manage'],
    payroll: [],
    benefits: [],
    performance: [],
    learning: [],
    reports: ['read'],
    settings: [],
    audit: [],
  },
  MANAGER: {
    employees: ['read'],
    departments: ['read'],
    locations: ['read'],
    jobs: ['read'],
    recruiting: ['read'],
    payroll: [],
    benefits: ['read'],
    performance: ['read', 'create', 'update'],
    learning: ['read'],
    reports: ['read'],
    settings: [],
    audit: [],
  },
  EMPLOYEE: {
    employees: [],  // Can only see own profile via My Info
    departments: [],
    locations: [],
    jobs: [],
    recruiting: [],
    payroll: [],
    benefits: ['read'],  // Can view own benefits
    performance: ['read'],  // Can view own performance
    learning: ['read'],  // Can view own learning
    reports: [],
    settings: [],
    audit: [],
  },
};

/**
 * Impersonated user info
 */
export interface ImpersonatedUser {
  userId: string;
  email: string;
  tenantSlug: string;
  tenantName?: string;
  role: string;
}

/**
 * Auth state interface
 */
interface AuthState {
  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;

  // User data (not persisted - loaded fresh on app start)
  user: UserProfile | null;

  // Current tenant context
  currentTenant: TenantContext | null;

  // Available tenants for switching
  availableTenants: TenantContext[];

  // Access token (in memory only - not persisted)
  accessToken: string | null;

  // Refresh token reference (actual token in HttpOnly cookie)
  hasRefreshToken: boolean;

  // Impersonation state
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  activeRequestId: string | null;

  // Actions
  setAuth: (params: {
    accessToken: string;
    user: UserProfile;
    tenant: TenantContext;
    availableTenants?: TenantContext[];
  }) => void;
  updateUser: (user: Partial<UserProfile>) => void;
  setCurrentTenant: (tenant: TenantContext) => void;
  setAvailableTenants: (tenants: TenantContext[]) => void;
  switchTenant: (tenantId: string) => Promise<boolean>;
  setAccessToken: (token: string) => void;
  clearAccessToken: () => void;
  setLoading: (loading: boolean) => void;
  clearAuth: () => void;
  reset: () => void;

  // Impersonation actions
  startImpersonation: (
    token: string,
    user: ImpersonatedUser,
    requestId: string
  ) => void;
  stopImpersonation: () => void;

  // Permission helpers
  hasPermission: (resource: PermissionResource, action: PermissionAction) => boolean;
  hasAnyPermission: (resource: PermissionResource, actions: PermissionAction[]) => boolean;
  hasRole: (role: TenantRole) => boolean;
  hasAnyRole: (roles: TenantRole[]) => boolean;
  isAdmin: () => boolean;
}

/**
 * Initial state for auth store
 */
const initialState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  currentTenant: null,
  availableTenants: [],
  accessToken: null,
  hasRefreshToken: false,
  isImpersonating: false,
  impersonatedUser: null,
  activeRequestId: null,
};

/**
 * Auth store for managing authentication state
 *
 * Note: Access tokens are stored in memory only (not persisted)
 * to prevent XSS attacks. Refresh tokens should be in HttpOnly cookies.
 */
export const useAuthStore = createStore<AuthState>('auth-store', (set, get) => ({
  ...initialState,

  setAuth: ({ accessToken, user, tenant, availableTenants }) => {
    set((state) => {
      state.accessToken = accessToken;
      state.user = user;
      state.currentTenant = tenant;
      state.availableTenants = availableTenants || [tenant];
      state.isAuthenticated = true;
      state.isLoading = false;
      state.hasRefreshToken = true;
    });
  },

  updateUser: (userUpdate) => {
    set((state) => {
      if (state.user) {
        state.user = { ...state.user, ...userUpdate };
      }
    });
  },

  setCurrentTenant: (tenant) => {
    set((state) => {
      state.currentTenant = tenant;
    });
  },

  setAvailableTenants: (tenants) => {
    set((state) => {
      state.availableTenants = tenants;
    });
  },

  switchTenant: async (tenantId) => {
    const { availableTenants } = get();
    const targetTenant = availableTenants.find((t) => t.id === tenantId);

    if (!targetTenant) {
      console.error('Tenant not found in available tenants');
      return false;
    }

    try {
      // Call the API to get new tokens scoped to the new tenant
      const response = await api.auth.switchTenant(tenantId);

      // Store both access and refresh tokens
      setTokens(response.accessToken, response.refreshToken);

      set((state) => {
        state.currentTenant = targetTenant;
        state.accessToken = response.accessToken;
      });

      // Reload the page to refresh all data for the new tenant context
      window.location.reload();

      return true;
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      return false;
    }
  },

  setAccessToken: (token) => {
    set((state) => {
      state.accessToken = token;
    });
  },

  clearAccessToken: () => {
    set((state) => {
      state.accessToken = null;
    });
  },

  setLoading: (loading) => {
    set((state) => {
      state.isLoading = loading;
    });
  },

  clearAuth: () => {
    // Clear persisted stores on logout
    clearPersistedStores();

    set((state) => {
      state.accessToken = null;
      state.user = null;
      state.currentTenant = null;
      state.availableTenants = [];
      state.isAuthenticated = false;
      state.isLoading = false;
      state.hasRefreshToken = false;
    });
  },

  reset: () => {
    set((state) => {
      Object.assign(state, initialState);
    });
  },

  // Permission helpers
  hasPermission: (resource, action) => {
    const { currentTenant } = get();
    if (!currentTenant) return false;

    const permissions = rolePermissions[currentTenant.role]?.[resource] || [];
    return permissions.includes(action) || permissions.includes('manage');
  },

  hasAnyPermission: (resource, actions) => {
    const { hasPermission } = get();
    return actions.some((action) => hasPermission(resource, action));
  },

  hasRole: (role) => {
    const { currentTenant } = get();
    return currentTenant?.role === role;
  },

  hasAnyRole: (roles) => {
    const { currentTenant } = get();
    return currentTenant ? roles.includes(currentTenant.role) : false;
  },

  isAdmin: () => {
    const { hasAnyRole } = get();
    return hasAnyRole(['SUPER_ADMIN', 'HR_ADMIN']);
  },

  // Impersonation actions
  startImpersonation: (token, user, requestId) => {
    // Save current admin token to sessionStorage (tab-scoped)
    const currentToken = get().accessToken;
    if (currentToken && typeof window !== 'undefined') {
      sessionStorage.setItem('hr_admin_token', currentToken);
    }

    set((state) => {
      state.accessToken = token;
      state.isImpersonating = true;
      state.impersonatedUser = user;
      state.activeRequestId = requestId;
    });

    // Store impersonation token as the active token
    if (typeof window !== 'undefined') {
      localStorage.setItem('accessToken', token);
    }
  },

  stopImpersonation: () => {
    // Restore admin token from sessionStorage
    let adminToken: string | null = null;
    if (typeof window !== 'undefined') {
      adminToken = sessionStorage.getItem('hr_admin_token');
      sessionStorage.removeItem('hr_admin_token');
    }

    set((state) => {
      state.isImpersonating = false;
      state.impersonatedUser = null;
      state.activeRequestId = null;
      if (adminToken) {
        state.accessToken = adminToken;
      }
    });

    // Restore admin token
    if (typeof window !== 'undefined' && adminToken) {
      localStorage.setItem('accessToken', adminToken);
    }

    // Reload to refresh data with admin context
    if (typeof window !== 'undefined') {
      window.location.href = '/admin';
    }
  },
}));

/**
 * Selector hooks for common auth state
 */
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useUser = () => useAuthStore((state) => state.user);
export const useCurrentTenant = () => useAuthStore((state) => state.currentTenant);
export const useCurrentRole = () => useAuthStore((state) => state.currentTenant?.role);
export const useAvailableTenants = () => useAuthStore((state) => state.availableTenants);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useIsPlatformAdmin = () => useAuthStore((state) => state.user?.isPlatformAdmin ?? false);

/**
 * Hook for checking permissions
 */
export function usePermission(resource: PermissionResource, action: PermissionAction): boolean {
  return useAuthStore((state) => state.hasPermission(resource, action));
}

/**
 * Hook for checking if user can perform any of the actions on a resource
 */
export function useAnyPermission(
  resource: PermissionResource,
  actions: PermissionAction[]
): boolean {
  return useAuthStore((state) => state.hasAnyPermission(resource, actions));
}
